// src/components/HRComponents/RegularizationForm.js
import React, { useState } from "react";
import { Upload, Clock, Info, FileText, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from "../../customStyles/SelectDropdown";
// Reusable helper function import
import { formatTo12Hour } from "../../helpers/timeHelper"; 

const RegularizationForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingAtt, setFetchingAtt] = useState(false);
  const [existingAtt, setExistingAtt] = useState(null);
  const [files, setFiles] = useState([]);

  // Split states to handle Date and Time separately for perfect responsiveness
  const [checkInDate, setCheckInDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  
  const [formData, setFormData] = useState({
    date: "",
    type: "missed_punch",
    requestedStatus: "present",
    requestedCheckIn: "",
    requestedCheckOut: "",
    reason: "",
    overrideAllPunches: false, // New Boolean state to pass to backend for merge vs reset
  });

  // SelectDropdown options
  const typeOptions = [
    { label: "Missed Punch", id: "missed_punch" },
    { label: "Wrong Entry", id: "wrong_time" },
  ];

  // Helper: Convert ISO Date to HTML datetime-local parts safely
  const formatToDateTimeLocal = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return ""; 
      const offset = d.getTimezoneOffset() * 60000;
      const localISOTime = new Date(d.getTime() - offset).toISOString().slice(0, 16);
      return localISOTime;
    } catch (err) {
      console.error("Error formatting date:", err);
      return "";
    }
  };

  const handleDateChange = async (date) => {
    setFormData((prev) => ({ ...prev, date }));
    setExistingAtt(null);
    if (!date) return;

    try {
      setFetchingAtt(true);
      
      const res = await api({
        url: `${SummaryApi.getDayAttendanceDetails.url}?date=${date}`,
        method: "GET"
      });
      
      if (res.data.success && res.data.data) {
        const att = res.data.data;
        setExistingAtt(att);
        
        const inDateTime = att.firstIn ? formatToDateTimeLocal(att.firstIn) : "";
        const outDateTime = att.lastOut ? formatToDateTimeLocal(att.lastOut) : "";

        if (inDateTime) {
          const [d, t] = inDateTime.split("T");
          setCheckInDate(d || date);
          setCheckInTime(t || "");
        } else {
          setCheckInDate(date);
          setCheckInTime("");
        }

        if (outDateTime) {
          const [d, t] = outDateTime.split("T");
          setCheckOutDate(d || date);
          setCheckOutTime(t || "");
        } else {
          setCheckOutDate(date);
          setCheckOutTime("");
        }

        setFormData(prev => ({
          ...prev,
          requestedCheckIn: inDateTime,
          requestedCheckOut: outDateTime,
          type: (att.firstIn && !att.lastOut) ? 'missed_punch' : prev.type
        }));
      } else {
        setCheckInDate(date);
        setCheckInTime("");
        setCheckOutDate(date);
        setCheckOutTime("");
        setFormData(prev => ({
          ...prev,
          requestedCheckIn: "",
          requestedCheckOut: ""
        }));
      }
    } catch (err) {
      console.error("Error fetching day attendance details:", err);
      setExistingAtt(null);
      setCheckInDate(date);
      setCheckInTime("");
      setCheckOutDate(date);
      setCheckOutTime("");
    } finally {
      setFetchingAtt(false);
    }
  };

  // Combine Date & Time inputs dynamically on change
  const handleCheckInChange = (newDate, newTime) => {
    setCheckInDate(newDate);
    setCheckInTime(newTime);
    const combined = newDate && newTime ? `${newDate}T${newTime}` : "";
    setFormData(prev => ({ ...prev, requestedCheckIn: combined }));
  };

  const handleCheckOutChange = (newDate, newTime) => {
    setCheckOutDate(newDate);
    setCheckOutTime(newTime);
    const combined = newDate && newTime ? `${newDate}T${newTime}` : "";
    setFormData(prev => ({ ...prev, requestedCheckOut: combined }));
  };

  // NEW: Quick-action to auto-fill inputs by clicking any log in sidebar
  const handleQuickSetFromLogs = (timeStr, type) => {
    const formattedLocal = formatToDateTimeLocal(timeStr);
    if (!formattedLocal) return;
    const [d, t] = formattedLocal.split("T");

    if (type === "in") {
      setCheckInDate(d);
      setCheckInTime(t);
      setFormData(prev => ({ ...prev, requestedCheckIn: formattedLocal }));
      // Using helper function for formatted toast message
      toast.info(`Set Requested Check-In to: ${formatTo12Hour(timeStr)}`);
    } else {
      setCheckOutDate(d);
      setCheckOutTime(t);
      setFormData(prev => ({ ...prev, requestedCheckOut: formattedLocal }));
      // Using helper function for formatted toast message
      toast.info(`Set Requested Check-Out to: ${formatTo12Hour(timeStr)}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    Object.keys(formData).forEach(key => data.append(key, formData[key]));
    files.forEach(file => data.append("files", file));

    try {
      const res = await api({
        url: SummaryApi.requestRegularization.url,
        method: SummaryApi.requestRegularization.method,
        data,
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.success) {
        toast.success("Request submitted successfully!");
        onSuccess();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* LEFT COLUMN: FORM */}
        <div className="lg:col-span-7 space-y-6">
          <div className="border-b border-gray-100 pb-4">
            <h3 className="text-lg font-bold text-gray-850">Regularization Details</h3>
            <p className="text-xs text-gray-400 mt-1">Please fill the actual punch details for rectification.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-2">Date</label>
                <input 
                  type="date" required 
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-sm font-medium h-[42px]" 
                  value={formData.date} 
                  onChange={e => handleDateChange(e.target.value)} 
                />
              </div>

              <div>
                <SelectDropdown
                  label="Type"
                  labelClassName="!text-[11px] !font-bold !text-gray-400 !uppercase !tracking-wide !mb-2 !block"
                  options={typeOptions}
                  value={formData.type}
                  onChange={(val) => setFormData({ ...formData, type: val })}
                  labelKey="label"
                  valueKey="id"
                  placeholder="Select Type"
                  ChildClassName="!bg-gray-50 !border-gray-200 !rounded-lg !h-[42px] !text-sm !font-medium"
                />
              </div>
            </div>

            {/* Split Date-Time Section: 100% Mobile Responsive */}
            <div className="bg-red-50/20 p-4 rounded-lg border border-red-100/50 space-y-4">
              <div>
                <label className="text-[10px] font-bold text-red-600 uppercase flex items-center gap-1.5 mb-2">
                  <Clock size={12}/> Req. Check-In
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="date" required 
                    className="w-full p-2.5 rounded-lg border border-red-200 text-xs font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-gray-750" 
                    value={checkInDate} 
                    onChange={e => handleCheckInChange(e.target.value, checkInTime)} 
                  />
                  <input 
                    type="time" required 
                    className="w-full p-2.5 rounded-lg border border-red-200 text-xs font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-gray-750" 
                    value={checkInTime} 
                    onChange={e => handleCheckInChange(checkInDate, e.target.value)} 
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-red-600 uppercase flex items-center gap-1.5 mb-2">
                  <Clock size={12}/> Req. Check-Out
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input 
                    type="date" required 
                    className="w-full p-2.5 rounded-lg border border-red-200 text-xs font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-gray-750" 
                    value={checkOutDate} 
                    onChange={e => handleCheckOutChange(e.target.value, checkOutTime)} 
                  />
                  <input 
                    type="time" required 
                    className="w-full p-2.5 rounded-lg border border-red-200 text-xs font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-gray-750" 
                    value={checkOutTime} 
                    onChange={e => handleCheckOutChange(checkOutDate, e.target.value)} 
                  />
                </div>
              </div>
            </div>

            {/* NEW Checkbox Option: Smart Merge vs Overwrite selection */}
            <div className="flex items-start gap-3 bg-gray-50/60 p-4 rounded-lg border border-gray-150">
              <input 
                type="checkbox" 
                id="overrideAllPunches"
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500/20 focus:ring-2 mt-0.5 accent-red-600 cursor-pointer"
                checked={formData.overrideAllPunches}
                onChange={e => setFormData({ ...formData, overrideAllPunches: e.target.checked })}
              />
              <label htmlFor="overrideAllPunches" className="cursor-pointer select-none">
                <span className="text-[11px] font-bold text-gray-700 block uppercase tracking-wide">Wipe out and reset all logs of the day</span>
                <span className="text-[10px] text-gray-400 block mt-1 leading-normal">
                  Check this option only if existing biometric punches are completely incorrect and you want to replace them with just the requested check-in and check-out times. Leave unchecked to safely preserve existing intermediate break logs (Smart Merge).
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide block">Reason</label>
              <textarea 
                required rows="3" 
                placeholder="Enter valid reason for regularization..."
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 text-sm transition-all" 
                value={formData.reason} 
                onChange={e => setFormData({...formData, reason: e.target.value})} 
              />
            </div>

            {/* Proof Uploader */}
            <div>
              <label className="text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-2">Proof / Attachment</label>
              <div className="border-2 border-dashed border-gray-200 hover:border-red-400 rounded-lg p-6 text-center relative transition-all bg-gray-50/50">
                <input 
                  type="file" multiple 
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" 
                  onChange={e => setFiles([...files, ...e.target.files])} 
                />
                <Upload className="mx-auto text-gray-400" size={24} />
                <p className="text-[11px] font-bold text-gray-600 mt-2 uppercase">Upload Images/Files</p>
                <p className="text-[9px] text-gray-400 mt-0.5">Click or drag your supporting proofs here</p>
              </div>
              
              {files.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {files.map((f, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1.5 text-[10px] bg-red-50 text-red-700 font-bold px-3 py-1.5 rounded-lg border border-red-100">
                      <FileText size={12} /> {f.name.slice(0, 15)}...
                    </span>
                  ))}
                </div>
              )}
            </div>

            <button 
              disabled={loading} 
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold uppercase text-sm shadow-md active:scale-95 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all duration-200"
            >
              {loading ? "Submitting Request..." : "Submit Request"}
            </button>
          </form>
        </div>

        {/* RIGHT COLUMN: DATA INSIGHT (Existing Attendance) */}
        <div className="lg:col-span-5">
          <div className="bg-gray-50/80 rounded-lg p-5 sm:p-6 border border-gray-100 h-full flex flex-col justify-between">
            <div>
              <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-5 flex items-center gap-2">
                <Info size={14} className="text-red-500" /> Current Day Insight
              </h4>

              {fetchingAtt ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                  <div className="w-8 h-8 border-4 border-red-100 border-t-red-600 rounded-full animate-spin"></div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Fetching records...</p>
                </div>
              ) : existingAtt ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Current Status</span>
                    <span className={`text-[10px] font-bold uppercase px-3 py-1 rounded-full ${
                      existingAtt.status === 'absent' ? 'bg-red-55 text-red-600 bg-red-100/50' : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                    }`}>
                      {existingAtt.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                      <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">First In</p>
                      <p className="text-sm font-extrabold text-gray-700">
                        {/* Using helper function for First In */}
                        {existingAtt.firstIn ? formatTo12Hour(existingAtt.firstIn) : "--:--"}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                      <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Last Out</p>
                      <p className={`text-sm font-extrabold ${existingAtt.lastOut ? 'text-gray-700' : 'text-red-500'}`}>
                        {/* Using helper function for Last Out */}
                        {existingAtt.lastOut ? formatTo12Hour(existingAtt.lastOut) : "MISSING"}
                      </p>
                    </div>
                  </div>

                  {existingAtt.punches?.length > 0 && (
                    <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
                      <p className="text-[10px] font-bold text-gray-400 uppercase border-b border-gray-100 pb-2">Logs Detected</p>
                      <div className="max-h-[160px] overflow-y-auto pr-1 space-y-2 custom-scrollbar">
                        {existingAtt.punches.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-50/50 p-2 rounded-lg text-[11px] font-semibold text-gray-600 border border-gray-100">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-red-50 text-red-600 rounded-full flex items-center justify-center font-bold text-[10px]">
                                {p.type?.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-xs font-bold text-gray-700 capitalize">{p.type}</span>
                            </div>
                            
                            {/* Quick Set buttons integration inside Logs list */}
                            <div className="flex items-center justify-between gap-2">
                               {/* Using helper function for Logs time display */}
                               <span className="text-[9px] text-gray-450 font-bold mr-1">{formatTo12Hour(p.time)}</span>
                               <div className="flex gap-1">
                                 <button
                                   type="button"
                                   onClick={() => handleQuickSetFromLogs(p.time, "in")}
                                   className="px-1.5 py-0.5 text-[8px] font-extrabold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-200 transition-colors"
                                   title="Set as Requested Check-In"
                                 >
                                   Set In
                                 </button>
                                 <button
                                   type="button"
                                   onClick={() => handleQuickSetFromLogs(p.time, "out")}
                                   className="px-1.5 py-0.5 text-[8px] font-extrabold text-red-750 bg-red-50 hover:bg-red-100 rounded border border-red-200 transition-colors"
                                   title="Set as Requested Check-Out"
                                 >
                                   Set Out
                                 </button>
                               </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center px-6 bg-white border border-gray-100 rounded-lg shadow-sm">
                  <Calendar size={36} className="text-red-200 mb-3" />
                  <p className="text-[11px] font-extrabold text-gray-400 uppercase leading-relaxed tracking-wider">
                    Select a date to view existing <br/> punch history here
                  </p>
                </div>
              )}
            </div>

            {existingAtt && !existingAtt.lastOut && (
              <div className="bg-amber-50 border border-amber-100 p-3.5 rounded-lg flex gap-3 mt-4">
                <Info size={18} className="text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-800 font-medium leading-relaxed">
                  System detected a <b>Missed Checkout</b>. Please provide your actual exit time in "Req. Check-Out" for correction.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegularizationForm;